import React, { useState, useEffect, forwardRef, useRef } from "react";
import styled from "styled-components";
import DynamicComponent from "../DynamicComponent";
import Image from "../Image";
import classNames from "classnames";
import IStyledProps from "../../utilities/IStyledProps";
import IEditableProps from "../../utilities/IEditableProps";
import {calculateDate} from "../../utilities/dateTime";
import { usePageProps } from '../../context/PagePropContext';
import BlogStyles from "./blogStyles";
import { isMobile } from "react-device-detect";

const BlogContainer = styled.div`
    .share-article {
        font-size: 0.611rem;
        font-weight: 300;
    }
    .description {
        background: #E2E2E233;
        border-radius: 0.444rem 0rem 0rem 0rem;
        padding: 1.333rem 0.889rem 1.333rem 0.889rem;
        font-size: 1rem;

        p {
            margin-bottom: 0;
        }
    }
        
    .blog-image-container {
        aspect-ratio: 3/2;

        @media (min-width: 992px) {
            margin-bottom: 1rem !important;
        }
    }

    .blog-category {
        cursor: pointer;
        transition: color 0.2s ease;
        
        &:hover {
            color: var(--red-rock) !important;
        }
    }
    
    @media (max-width: 991px) {
        .blog-title {
            margin-top: 0.5rem !important;

            /* Add normal line height for non-featured blog titles */
            &:not(.featured-title) {
                display: block;
                line-height: 1.2 !important;
            }
        }
            
        .blog-post-age {
            font-size: 0.667rem !important;
        }
            
        .blog-category {
            font-size: 0.667rem !important;
        } 
            
        .blog-meta {
            line-height: 1 !important;
        }
    }
`;

function BlogLink({ href, children, ...props }) {
    if (href) {
        return (
            <a href={href} className="text-decoration-none" {...props}>
                {children}
            </a>
        );
    }

    return children;
}

export interface IBlogPropsV2 extends IStyledProps, IEditableProps {
    dateType?: string;
    dateFormat?: string;
    src?: string;
    alt?: string;
    description?: string;
    headerAs?: string;
    headerClassName?: string;
    href?: string;
    title: string;
    timestamp: number | string;
    tags: string;
    testIdPrefix?: string;
    isMainArticle?: boolean;
    // DO WE EVEN NEED THIS?
    imageSize?: 'featured' | 'secondary';
    categories?: string[];
    onCategoryClick?: (category: string) => void;
}

const BlogV2 = forwardRef((props: IBlogPropsV2, ref: any) => {
    const { 
        cmsId, 
        editorClassObj, 
        headerAs = "big", 
        // description,
        title, 
        timestamp: blogPropsTimestamp, 
        tags: tagsProp, 
        src, 
        alt, 
        headerClassName, 
        href, 
        dateType = "date", 
        dateFormat = "MMMM d, yyyy", 
        testIdPrefix = '', 
        isMainArticle = true,
        imageSize = 'grid',
        categories = [],
        onCategoryClick = () => {},
    } = props;
    
    const { timestamp: pagePropsTimestamp, blogTitle, imageSrc, imageAlt } = usePageProps();
    const displaytitle = title ?? blogTitle;
    const displayDate = blogPropsTimestamp ?? pagePropsTimestamp;
    const displaySrc = src ?? imageSrc;
    const displayAlt = alt ?? imageAlt;
    const showMainBlogHeader = headerAs === 'big' || headerAs === 'h2';
    const headersMap = { big: 'h2', small: 'h5' };
    const headerTag = headersMap[headerAs] || headerAs;
    const [postAge, setPostAge] = useState(calculateDate(displayDate, dateType, dateFormat));
    const [url, setUrl] = useState(null);
    const dateRef = useRef(null);
    const mainRef = useRef(ref || null);

    const handleCategoryClick = (e: React.MouseEvent, category: string) => {
        e.preventDefault();
        e.stopPropagation(); // Prevent event bubbling
        onCategoryClick(category);
    };

    useEffect(() => {
        if (window) {
            setUrl(window.location.href);
        }
    }, []);

    useEffect(() => {
        setPostAge(calculateDate(displayDate, dateType, dateFormat));
    }, [displayDate, dateType]);

    useEffect(() => {
        if (dateRef.current && mainRef.current) {
            const previousElement = mainRef.current.previousElementSibling;
            if (!previousElement || !(previousElement.classList.contains('yt-thumbnail-container') || previousElement.classList.contains('yt-video-container'))) {
                return;
            };

            const parentElement = previousElement.parentElement;
            if (parentElement && dateRef.current && !document.body.contains(dateRef.current)) {
                dateRef.current.setAttribute('style', 'margin-bottom: 0.611rem;');
                parentElement.insertBefore(dateRef.current, previousElement);
            }
        }

        return () => {
            if (dateRef.current && dateRef.current.parentElement) {
                dateRef.current.parentElement.removeChild(dateRef.current);
            }
        };
    }, [url, dateRef, mainRef]);

    return (
        <div ref={mainRef} data-id={cmsId} className={classNames(editorClassObj, 'mb-4')}>
            {showMainBlogHeader && <BlogStyles />}
            <BlogContainer>
                <BlogLink data-test-id={`${testIdPrefix}blog-title-link`} href={href}>
                    {/* Blog image */}
                    {displaySrc && (
                        <Image className={`W-100 mb-2 blog-image-container`} dataTestId="blog-image" src={displaySrc} alt={displayAlt} />
                    )}
                    {/* Meta info: categories and date */}
                    <div className="blog-meta">
                        {categories && categories.length > 0 && (
                            <span 
                                className="text-muted h6 mr-1 blog-category"
                                onClick={(e) => handleCategoryClick(e, categories[0])}
                                data-test-id={`${testIdPrefix}blog-category`}
                            >
                                {categories[0]}
                            </span>
                        )}

                        <span className="text-dark blog-post-age" ref={dateRef} data-test-id="blog-date">
                            &nbsp;• &nbsp;
                            {postAge}
                        </span>
                    </div>
                    
                    {/* Blog title */}
                    <DynamicComponent 
                        data-test-id={`${testIdPrefix}blog-title`} 
                        as={ isMobile ? (headerTag === "h5" ? "small" : "h5") : headerTag} 
                        className={classNames(
                            headerClassName,  
                            "text-dark",
                            "blog-title",
                            isMainArticle ? "featured-title" : "",
                            imageSize === 'featured' ? "mb-2" : null
                        )}
                    >
                        <strong>{displaytitle}</strong>
                    </DynamicComponent>
                </BlogLink>
            </BlogContainer>
        </div>
    );
});

export default BlogV2;